import React, { useMemo, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { useGetHintCoverageGrid } from '~/hooks/apis/connection-google/useHintCoverageGrid';

import { HintCoverageGridCell, HintCoverageGridParams } from '~/services/ConnectionGoogleService';

import TierTotalsLegend from './TierTotalsLegend';
import { formatPct, formatRange, getCellColor } from './utils';

interface Props {
  accountType: HintCoverageGridParams['accountType'];
  windowHours: number;
}

const DEFAULT_MAX_LOS = 14;

const stickyColumnSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: 'action.hover',
  whiteSpace: 'nowrap',
} as const;
const dataCellSx = { whiteSpace: 'nowrap' } as const;

export default function HintCoverageGridSummary({ accountType, windowHours }: Props) {
  const theme = useTheme();
  const [showAllLos, setShowAllLos] = useState(false);

  const { data, isLoading, error } = useGetHintCoverageGrid({ accountType, windowHours });

  const { abwRanges, losRanges, cellMap } = useMemo(() => {
    if (!data?.cells) {
      return { abwRanges: [], losRanges: [], cellMap: new Map<string, HintCoverageGridCell>() };
    }

    const abwSet = new Map<string, HintCoverageGridCell['abwRange']>();
    const losSet = new Map<string, HintCoverageGridCell['losRange']>();
    const map = new Map<string, HintCoverageGridCell>();

    for (const cell of data.cells) {
      const abwKey = `${cell.abwRange.min}-${cell.abwRange.max}`;
      const losKey = `${cell.losRange.min}-${cell.losRange.max}`;
      abwSet.set(abwKey, cell.abwRange);
      losSet.set(losKey, cell.losRange);
      map.set(`${abwKey}|${losKey}`, cell);
    }

    return {
      abwRanges: Array.from(abwSet.values()).sort((a, b) => a.min - b.min),
      losRanges: Array.from(losSet.values()).sort((a, b) => a.min - b.min),
      cellMap: map,
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error.message || 'Failed to load hint coverage summary'}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const visibleLosRanges = showAllLos ? losRanges : losRanges.filter((r) => r.max <= DEFAULT_MAX_LOS);

  return (
    <Box mt={2}>
      <TierTotalsLegend tierTotals={data.tierTotals} />

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{ ...stickyColumnSx, zIndex: 3, bgcolor: 'action.selected', minWidth: theme.spacing(12) }}
              >
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  ABW (days) →
                </Typography>
                <br />
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  LOS (nights) ↓
                </Typography>
              </TableCell>
              {abwRanges.map((abwRange) => (
                <TableCell key={abwRange.min} align="center" sx={{ minWidth: theme.spacing(14) }}>
                  {formatRange(abwRange)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLosRanges.map((losRange) => (
              <TableRow key={losRange.min}>
                <TableCell align="center" sx={stickyColumnSx}>
                  <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                    {formatRange(losRange)}
                  </Typography>
                </TableCell>
                {abwRanges.map((abwRange) => {
                  const cellKey = `${abwRange.min}-${abwRange.max}|${losRange.min}-${losRange.max}`;
                  const cell = cellMap.get(cellKey);

                  if (!cell) {
                    return (
                      <TableCell key={abwRange.min} align="center" sx={{ ...dataCellSx, bgcolor: 'background.paper' }}>
                        —
                      </TableCell>
                    );
                  }

                  return (
                    <Tooltip
                      key={abwRange.min}
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            ABW: {formatRange(cell.abwRange)} days, LOS: {formatRange(cell.losRange)} nights
                          </Typography>
                          <Typography variant="caption" display="block">
                            HIGH: {cell.high.served} served ({formatPct(cell.high.pct)})
                          </Typography>
                          <Typography variant="caption" display="block">
                            MEDIUM: {cell.medium.served} served ({formatPct(cell.medium.pct)})
                          </Typography>
                          <Typography variant="caption" display="block">
                            LOW: {cell.low.served} served ({formatPct(cell.low.pct)})
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <TableCell
                        align="center"
                        sx={{ ...dataCellSx, bgcolor: getCellColor(theme, cell), cursor: 'default' }}
                      >
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: 'success.main', fontWeight: theme.typography.fontWeightMedium }}
                        >
                          H: {formatPct(cell.high.pct)}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: 'warning.main' }}>
                          M: {formatPct(cell.medium.pct)}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: 'error.main' }}>
                          L: {formatPct(cell.low.pct)}
                        </Typography>
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {losRanges.some((r) => r.max > DEFAULT_MAX_LOS) && (
        <Box mt={1}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAllLos((prev) => !prev)}
            startIcon={showAllLos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showAllLos ? 'Hide extended LOS (15+ nights)' : 'Show extended LOS (15+ nights)'}
          </Button>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" mt={1}>
        Generated at {new Date(data.generatedAt).toLocaleString()} — ABW ranges: 0–330 days, LOS ranges: 1–28 nights
      </Typography>
    </Box>
  );
}
